import { FC, Fragment, useEffect, useId, useState } from 'react'
import type { MouseEvent } from 'react'
import Bedroom from '@/components/icons/map/filter/bedroom'
import { Box, Popover, Typography } from '@mui/material'
import { difference } from 'lodash'
import { statsigLog } from '@/lib/statsigAndGa'
import CloseCircle from '@/components/icons/map/filter/closeCircle'
import { BEDROOM_DEFAULT_LABEL, BEDROOM_LIST } from './filterConfig'

interface BedroomFilterProps {
  onApply: (bedsFilterBy: number[]) => void
  bedroom: number[] | null
}

const calcLabel = (bedroom: number[]) => {
  if (bedroom.length === 0 || bedroom.includes(-1)) {
    return 'Bedroom'
  }
  if (bedroom.length === 1 && bedroom[0] === 0) {
    return 'Studio'
  }
  const selectedLabels = new Set<string>()
  bedroom.forEach(v => {
    if (v === 0) {
      selectedLabels.add('Studio')
    } else if (v < 3) {
      selectedLabels.add(String(v))
    } else {
      selectedLabels.add('3+')
    }
  })
  return `${Array.from(selectedLabels).join(', ')} bed`
}
const BedroomFilter: FC<BedroomFilterProps> = props => {
  const { onApply, bedroom } = props
  const [open, setOpen] = useState(false)
  const popoverId = useId()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selected, setSelected] = useState<number[]>(bedroom ?? [])

  const [label, setLabel] = useState(BEDROOM_DEFAULT_LABEL)

  const onClickButton = (event: MouseEvent<HTMLButtonElement>) => {
    statsigLog('bedroom_filter_click')
    setAnchorEl(event.currentTarget)
    setOpen(true)
  }
  const onSelectBedroom = (s: number | number[]) => {
    let newValue = [...selected]
    // 选择了any
    if (s === -1) {
      newValue = [-1]
    } else {
      // 选择了其他具体的 取消any
      newValue = newValue.filter(v => v !== -1)

      if (Array.isArray(s)) {
        // 选择了3+
        if (newValue.includes(s[0])) {
          newValue = difference(newValue, s)
        } else {
          newValue = [...newValue, ...s]
        }
      } else if (newValue.includes(s)) {
        newValue = newValue.filter(v => v !== s)
      } else {
        newValue.push(s)
      }
    }
    setSelected(newValue)
  }

  const onClickClear = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    const nullBedroom = []
    setLabel(calcLabel(nullBedroom))
    onApply(nullBedroom)
  }

  const onPopoverReset = () => {
    setSelected(Array.isArray(bedroom) ? [...bedroom] : [])
  }

  const onPopoverApply = () => {
    setOpen(false)
    // any的情况
    const currentSelected = selected.includes(-1) ? [] : selected
    setLabel(calcLabel(currentSelected))
    onApply(currentSelected)
  }

  useEffect(() => {
    const s = Array.isArray(bedroom) ? [...bedroom] : []
    setLabel(calcLabel(s))
    setSelected(s)
  }, [bedroom])
  return (
    <>
      <Box
        aria-describedby={popoverId}
        component="button"
        onClick={onClickButton}
        className={`flex items-center gap-2 py-[6px] px-4 border border-gray-200 rounded-20 hover:bg-gray-100 ${
          label !== BEDROOM_DEFAULT_LABEL ? 'border-primary bg-gray-100' : 'border-gray-200'
        }`}
      >
        <Bedroom className="w-4 h-4" />
        <Typography className="text-primary text-sm">{label}</Typography>
        {label !== BEDROOM_DEFAULT_LABEL && (
          <CloseCircle className="w-4 h-4" onClick={onClickClear} />
        )}
      </Box>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={() => {
          setOpen(false)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        classes={{
          paper: 'rounded-xl bg-white p-4 mt-4 shadow-[0_1px_7px_0_#242A2E1A]'
        }}
      >
        <Box component="div" className="flex flex-wrap items-center gap-3 w-80">
          {BEDROOM_LIST.map(room => {
            const isSelected = selected.includes(
              Array.isArray(room.value) ? room.value[0] : room.value
            )
            return (
              <Box
                key={room.label}
                component="button"
                onClick={() => onSelectBedroom(room.value)}
                className={`text-sm text-primary px-4 py-2 border rounded-20 ${
                  isSelected ? 'border-primary bg-gray-100' : 'border-gray-200'
                }`}
              >
                {room.label}
              </Box>
            )
          })}
        </Box>
        <Box component="div" className="mt-4 flex justify-between items-end">
          <Typography
            onClick={onPopoverReset}
            className="text-primary font-semibold underline cursor-pointer"
          >
            Reset
          </Typography>
          <Box
            component="button"
            onClick={onPopoverApply}
            className="bg-brand text-white font-semibold rounded-xl py-2 px-6"
          >
            Apply
          </Box>
        </Box>
      </Popover>
    </>
  )
}

export default BedroomFilter
